import { Link } from "react-router-dom";
import type { EventItem } from "@/types";
import SectionHeading from "@/components/base/SectionHeading";
import StatusBadge from "@/components/base/StatusBadge";
import { formatDateShort, getWeekday } from "@/utils/format";

interface ResultsBoardProps {
  events: EventItem[];
}

export default function ResultsBoard({ events }: ResultsBoardProps) {
  return (
    <section className="w-full bg-background-50 border-y border-background-200">
      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <SectionHeading
          eyebrow="Latest Results"
          title="最新赛果汇总"
          desc="已结束比赛的最终比分与赛况回顾，实时同步每日战报。"
          action={
            <Link
              to="/events"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
            >
              更多赛果 <i className="ri-arrow-right-line" />
            </Link>
          }
        />

        {events.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
            <i className="ri-trophy-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-500">暂无赛果记录，敬请期待</p>
          </div>
        ) : (
          <div className="rounded-lg border border-background-200 overflow-hidden">
            {events.map((e, idx) => (
              <Link
                key={e.id}
                to={`/event/${e.id}`}
                className={`flex items-center gap-3 md:gap-6 px-4 md:px-6 py-4 transition-colors hover:bg-primary-50/50 ${
                  idx !== 0 ? "border-t border-background-100" : ""
                }`}
              >
                <div className="hidden sm:flex flex-col items-center w-16 shrink-0">
                  <span className="font-heading text-sm text-foreground-900">{formatDateShort(e.date)}</span>
                  <span className="text-[11px] text-foreground-500">{getWeekday(e.date)}</span>
                </div>
                <span className="hidden md:inline-block px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-label shrink-0">
                  {e.categoryName}
                </span>
                <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                  <span className="text-sm md:text-base text-foreground-900 truncate">{e.home.name}</span>
                  <span className="shrink-0 flex items-center gap-2 font-heading text-lg md:text-xl">
                    <span className={(e.homeScore ?? 0) > (e.awayScore ?? 0) ? "text-primary-600" : "text-foreground-800"}>
                      {e.homeScore}
                    </span>
                    <span className="text-foreground-300 text-sm">:</span>
                    <span className={(e.awayScore ?? 0) > (e.homeScore ?? 0) ? "text-primary-600" : "text-foreground-800"}>
                      {e.awayScore}
                    </span>
                  </span>
                  <span className="text-sm md:text-base text-foreground-900 truncate text-right">{e.away.name}</span>
                </div>
                <div className="hidden lg:block shrink-0">
                  <StatusBadge status={e.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}